import { NotificationBadgeModel } from 'api-hooks/notification/model';
import { RefundTabEnum } from 'api-hooks/refund/common';
import { RefundStatusEnum } from 'common/constants/enum';
import Separator from 'components/common/separator';
import Tabs from 'components/common/tabs';
import { NotificationBadge } from 'legacy-modules/styles';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';

interface Props {
  setActiveKey: (value: React.SetStateAction<React.Key>) => void;
  activeKey: React.Key;
  setStatus: (value: React.SetStateAction<string | undefined>) => void;
  badge?: { [key: string]: NotificationBadgeModel };
}

export function useRefundTabStatus() {
  const processedStatus = [RefundStatusEnum.Processed].join('|');
  const needApprovalStatus = [
    RefundStatusEnum.Draft,
    RefundStatusEnum.PendingApproval,
    RefundStatusEnum.DraftCancel,
    RefundStatusEnum.PendingCancel,
  ].join('|');
  const rejectedApprovalStatus = [RefundStatusEnum.RejectedApproval].join('|');
  const completeStatus = [
    RefundStatusEnum.Completed,
    RefundStatusEnum.Cancel,
  ].join('|');
  const allStatus = [
    processedStatus,
    needApprovalStatus,
    rejectedApprovalStatus,
    completeStatus,
  ].join('|');

  const tabStatus = {
    '0': allStatus,
    '1': processedStatus,
    '2': needApprovalStatus,
    '3': rejectedApprovalStatus,
    '4': completeStatus,
  } as const;

  return {
    processedStatus,
    needApprovalStatus,
    rejectedApprovalStatus,
    completeStatus,
    allStatus,
    tabStatus,
  };
}

export default function RefundListTabs(props: Props) {
  const { t } = useTranslation();
  const { activeKey, setActiveKey, setStatus, badge } = props;

  const { tabStatus } = useRefundTabStatus();

  return (
    <Tabs
      activeKey={activeKey}
      onChangeActive={({ activeKey }) => {
        setActiveKey(activeKey);
        setStatus(tabStatus[activeKey]);
      }}
      items={[
        { title: t('common:all'), component: <></> },
        {
          title: (
            <>
              {t('common:processed')}
              {!!badge?.[RefundTabEnum.Processed] &&
                !!badge?.[RefundTabEnum.Processed].count && (
                  <>
                    <Separator gap={8} />
                    <NotificationBadge
                      style={{
                        backgroundColor:
                          badge?.[RefundTabEnum.Processed].background_color,
                        color: badge?.[RefundTabEnum.Processed].color,
                      }}
                    >
                      {badge?.[RefundTabEnum.Processed].count}
                    </NotificationBadge>
                  </>
                )}
            </>
          ),
          component: <></>,
        },
        {
          title: (
            <>
              {t('common:need_approval')}
              {!!badge?.[RefundTabEnum.NeedApproval] &&
                !!badge?.[RefundTabEnum.NeedApproval].count && (
                  <>
                    <Separator gap={8} />
                    <NotificationBadge
                      style={{
                        backgroundColor:
                          badge?.[RefundTabEnum.NeedApproval].background_color,
                        color: badge?.[RefundTabEnum.NeedApproval].color,
                      }}
                    >
                      {badge?.[RefundTabEnum.NeedApproval].count}
                    </NotificationBadge>
                  </>
                )}
            </>
          ),
          component: <></>,
        },
        {
          title: (
            <>
              {t('common:rejected_approval')}
              {!!badge?.[RefundTabEnum.RejectedApproval] &&
                !!badge?.[RefundTabEnum.RejectedApproval].count && (
                  <>
                    <Separator gap={8} />
                    <NotificationBadge
                      style={{
                        backgroundColor:
                          badge?.[RefundTabEnum.RejectedApproval]
                            .background_color,
                        color: badge?.[RefundTabEnum.RejectedApproval].color,
                      }}
                    >
                      {badge?.[RefundTabEnum.RejectedApproval].count}
                    </NotificationBadge>
                  </>
                )}
            </>
          ),
          component: <></>,
        },
        { title: t('common:complete'), component: <></> },
      ]}
      overrideStyles={{
        tabList: { minHeight: '0px' },
        tab: { minHeight: '0px', height: '0', flexGrow: '0' },
        tabPanel: { minHeight: '0px', height: '0', flexGrow: '0' },
      }}
    />
  );
}
